// Security Centre — admin-only view of MFA adoption, audit trail, alerts, backups, policies.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { API, btnGhost } from '../components/security/ui';
import { OverviewTab } from '../components/security/OverviewTab';
import { AuditTab, AlertsTab, BackupsTab } from '../components/security/OpsTabs';
import { UsersTab, PoliciesTab, MyAccountTab } from '../components/security/AdminTabs';
import { DataTransfersTab } from '../components/security/DataTransfersTab';
import { JarvisAuditTab } from '../components/security/JarvisAuditTab';
import { EvidenceTab } from '../components/security/EvidenceTab';

const TABS = ['Overview', 'Data transfers', 'Jarvis audit', 'Evidence', 'Audit log', 'Alerts', 'Backups', 'Users & MFA', 'Policies', 'My account'];

export default function SecurityCentre() {
  const { logout, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([axios.get(`${API}/api/security/overview`), axios.get(`${API}/api/security/settings`)]);
      setData(o.data); setSettings(s.data);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true); else toast.error('Could not load Security Centre');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied || (!isAdmin() && !data)) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-600" data-testid="security-denied">Administrator access is required for the Security Centre.</div>;
  }
  if (!data) return <div className="max-w-6xl mx-auto px-4 py-16 text-center text-slate-400">Loading Security Centre…</div>;

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      <Header />
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="security-centre-page">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#2E333B] text-white"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <h1 className="text-2xl font-extrabold text-[#2E333B] tracking-tight">Security Centre</h1>
            <p className="text-sm text-slate-500">Defence-in-depth status for DerwentAI — encryption, access control, audit trail, backups and alerts.</p>
          </div>
        </div>
        <button className={btnGhost} onClick={load} disabled={loading} data-testid="security-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
      </div>

      <div className="mb-5 flex flex-wrap gap-1 rounded-2xl bg-white p-1.5 ring-1 ring-slate-200/70 w-fit" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`security-tab-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className={`rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors ${tab === t ? 'bg-[#2E333B] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t}{t === 'Alerts' && data.alerts_open > 0 && <span className="ml-1.5 rounded-full bg-rose-500 px-1.5 text-[10px] text-white">{data.alerts_open}</span>}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <OverviewTab data={data} />}
      {tab === 'Data transfers' && <DataTransfersTab />}
      {tab === 'Jarvis audit' && <JarvisAuditTab />}
      {tab === 'Evidence' && <EvidenceTab />}
      {tab === 'Audit log' && <AuditTab />}
      {tab === 'Alerts' && <AlertsTab settings={settings} onSettingsSaved={load} />}
      {tab === 'Backups' && <BackupsTab onChanged={load} />}
      {tab === 'Users & MFA' && <UsersTab users={data.users} permissionChanges={data.permission_changes} />}
      {tab === 'Policies' && <PoliciesTab checklist={data.checklist} onChanged={load} />}
      {tab === 'My account' && <MyAccountTab onLogout={logout} />}
    </div>
    </div>
  );
}
